<template>
  <div class="h-52">
    <Pie
      :data="chartData"
      :options="chartOptions"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="plugins"
      :css-classes="cssClasses"
      :styles="styles"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script setup lang="ts">
import { australianCurrency } from '@/helpers';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { computed } from 'vue';
import { Pie } from 'vue-chartjs';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
);

const props = defineProps<{
  cumulativeAmounts: number[];
  totalBudget: number;
  approvedVariationsBudget: number;
  hoveredIndex?: number;
}>();

// Chart configuration
const chartId = 'head-contract-work-breakdown-pie-chart';
const datasetIdKey = 'label';
const plugins = [];
const cssClasses = '';
const styles = {};
const width = undefined;
const height = undefined;

const chartData = computed(() => {
  // Use the last index by default, or the hovered index if provided
  const index = props.hoveredIndex !== undefined ? props.hoveredIndex : (props.cumulativeAmounts.length - 1);
  const claimCertifiedTotal = props.cumulativeAmounts[index] || 0;
  const totalBudget = props.totalBudget + props.approvedVariationsBudget;
  const remainingBudget = Math.max(totalBudget - claimCertifiedTotal, 0);

  return {
    labels: [
      'Work Done',
      'Remaining',
    ],

    datasets: [{
      data: [claimCertifiedTotal, remainingBudget],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(239, 68, 68, 0.8)',
      ],

      borderColor: [
        'rgba(59, 130, 246, 1)',
        'rgba(239, 68, 68, 1)',
      ],

      borderWidth: 2,
      hoverOffset: 8,
    }],
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },

    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      titleColor: '#ffffff',
      bodyColor: '#ffffff',
      borderColor: 'rgba(255, 255, 255, 0.1)',
      borderWidth: 1,
      cornerRadius: 8,
      callbacks: {
        label(context: any) {
          const total = props.totalBudget + props.approvedVariationsBudget;
          const percentage = total > 0 ? (context.parsed / total * 100).toFixed(1) : '0';

          return `${percentage}%`;
        },
      },
    },
  },

  elements: {
    arc: {
      borderWidth: 2,
    },
  },
}));
</script>

<style scoped></style>
